/* eslint-disable @typescript-eslint/no-unused-vars */
import "./MonthlySection.scss";

import { withErrorBoundary } from "components/fallback/withErrorBoundary";
import { SelectInput } from "components/Inputs/SelectInput";
import { LoadingSection } from "components/LoadingSection";
import { getMonth, isAfter, isBefore } from "date-fns";
import { useLargeTabContext } from "pages/UsagePage/components/LargeTabPanel/LargeTabProvider";
import { useGetUsagePerMonthQuery } from "queries/billingApi.flickapi";
import { expireTime } from "queries/queries.utils";
import React, { ChangeEvent, useMemo, useState } from "react";
import { useAppSelector } from "reduxUtils/hook";
import {
  isUserGstRegistered,
  selectBillingDerivedForMonthlyRequest,
} from "selectors/billingEntitySelector";
import { jwtClient } from "utils/jwtClient";

import {
  MonthlyUsageChartProvider,
  useMonthlyUsageChart,
} from "../MonthlyUsageChartProvider";
import { MonthDetail } from "./MonthDetail";
import { MonthlySolarChart } from "./MonthlySolarChart";
import { MonthlyUsageChart } from "./MonthlyUsageChart";
import { processAnnualAggregatesData } from "./MonthlyUsageChart/MonthlyUsageChart.utils";
import { MonthSolarDetail } from "./MonthSolarDetail";

const selectOptions = [
  {
    label: "Dollars",
    value: "cost",
  },
  {
    label: "kWh",
    value: "usage",
  },
];

export const MonthlySection = withErrorBoundary(() => {
  const [displayType, setDisplayType] = useState(selectOptions[0].value);
  const { selectedMonthIndex } = useMonthlyUsageChart();

  const { customerNumber, supplyNodeRef } = useAppSelector(
    (store) => store.currentAccount,
  );
  const { activeTab } = useLargeTabContext();

  const isTaxInclusive = useAppSelector(isUserGstRegistered);

  const { thisYearStartAt, thisYearEndAt, lastYearStartAt, lastYearEndAt } =
    useAppSelector(selectBillingDerivedForMonthlyRequest);

  const {
    data: thisYearMonthlyData,
    isError: isThisYearError,
    isFetching: isThisYearLoading,
    error: thisYearError,
  } = useGetUsagePerMonthQuery(
    {
      customerNumber,
      startAt: thisYearStartAt,
      endAt: thisYearEndAt,
      jwtClient,
    },
    {
      skip:
        !customerNumber || !supplyNodeRef || !thisYearStartAt || !thisYearEndAt,
      refetchOnMountOrArgChange: expireTime,
    },
  );

  const { data: lastYearData, isLoading: isLastYearLoading } =
    useGetUsagePerMonthQuery(
      {
        customerNumber,
        startAt: lastYearStartAt,
        endAt: lastYearEndAt,
        jwtClient,
      },
      {
        skip:
          !customerNumber ||
          !lastYearStartAt ||
          !lastYearEndAt ||
          !supplyNodeRef,
        refetchOnMountOrArgChange: expireTime,
      },
    );

  const thisYearPrepped = useMemo(
    () =>
      processAnnualAggregatesData(thisYearMonthlyData, isTaxInclusive).map(
        (month) => {
          if (Number(month.month) === getMonth(new Date()) + 1) {
            return {
              ...month,
              usage: 0,
              cost: 0,
              spend: 0,
              earned: 0,
              consumption: 0,
              generation: 0,
            };
          }
          return month;
        },
      ),
    [thisYearMonthlyData, isTaxInclusive],
  );
  const lastYearPrepped = useMemo(
    () => processAnnualAggregatesData(lastYearData, isTaxInclusive),
    [lastYearData, isTaxInclusive],
  );

  const handleChangeDisplayTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setDisplayType(e.target.value);
  };

  if ((isThisYearLoading && !isThisYearError) || isLastYearLoading) {
    return <LoadingSection />;
  }

  if (isThisYearError) {
    throw thisYearError;
  }

  return (
    <div className="background--white">
      <div className="monthly-section-heading">
        <h2 className="section-title heading h5">Usage details</h2>
        <SelectInput
          ariaLabel="Dollars or kWh chart disply select"
          fullWidth={false}
          name="display-type"
          defaultOption={selectOptions[0]}
          extraClasses="usage-display-type-select"
          options={selectOptions}
          handleOnChange={handleChangeDisplayTypeChange}
          value={displayType}
        />
      </div>
      {activeTab === "solar" ? (
        <div className="monthly-section-container">
          <MonthlySolarChart
            displayType={displayType}
            thisYearData={thisYearPrepped}
          />
          <MonthSolarDetail
            monthThisYear={thisYearPrepped[selectedMonthIndex]}
            monthLastYear={lastYearPrepped[selectedMonthIndex]}
          />
        </div>
      ) : (
        <div className="monthly-section-container">
          <MonthlyUsageChart
            displayType={displayType}
            thisYearData={thisYearPrepped}
            lastYearData={lastYearPrepped}
          />
          <MonthDetail
            monthThisYear={thisYearPrepped[selectedMonthIndex]}
            monthLastYear={lastYearPrepped[selectedMonthIndex]}
          />
        </div>
      )}
    </div>
  );
});

export const MonthlySectionWrapper = () => {
  return (
    <MonthlyUsageChartProvider>
      <MonthlySection componentTitle="Monthly details" />
    </MonthlyUsageChartProvider>
  );
};
